import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getServerStatusHistoryAPI, getServerDetailsAPI } from '../services/serverService';

const ServerStatusHistoryPage = () => {
    const { serverId } = useParams();
    const [history, setHistory] = useState([]);
    const [serverName, setServerName] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            try {
                const [histRes, serverRes] = await Promise.all([
                    getServerStatusHistoryAPI(serverId),
                    getServerDetailsAPI(serverId)
                ]);
                setHistory(histRes.data.data);
                setServerName(serverRes.data.data.companyName);
            } catch (error) {
                console.error("Failed to load history", error);
            } finally {
                setIsLoading(false);
            }
        };
        loadData();
    }, [serverId]);

    const formatDate = (isoString) => {
        return new Date(isoString).toLocaleString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    // --- CSV EXPORT LOGIC ---
    const exportToCSV = () => {
        if (!history || history.length === 0) return;
        
        const headers = ['Time', 'Action', 'Performed By', 'Reason', 'Data Snapshot'];
        const rows = history.map(entry => {
            // Stringify JSON and escape quotes for CSV compatibility
            const snapshotStr = entry.snapshot 
                ? JSON.stringify(entry.snapshot).replace(/"/g, '""') 
                : 'No snapshot available';
            
            return [
                `"${formatDate(entry.timestamp)}"`,
                `"${entry.action}"`,
                `"${entry.performedBy}"`,
                `"${entry.reason ? entry.reason.replace(/"/g, '""') : ''}"`,
                `"${snapshotStr}"` 
            ];
        });
        
        const csvContent = [headers.join(','), ...rows.map(e => e.join(','))].join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        
        link.setAttribute("href", url);
        link.setAttribute("download", `Audit_Logs_${serverName.replace(/\s+/g, '_')}.csv`);
        link.style.visibility = 'hidden';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="page-container" style={{ padding: '30px', backgroundColor: '#f8fafc', minHeight: '100vh' }}>
            <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: '24px', color: '#1e293b' }}>Audit & Status History</h1>
                    <p style={{ margin: '5px 0 0', color: '#64748b' }}>Server: <strong>{serverName}</strong></p>
                </div>
                
                <button 
                    onClick={exportToCSV}
                    disabled={isLoading || history.length === 0}
                    style={{
                        padding: '10px 20px', backgroundColor: '#10b981', color: 'white', border: 'none', 
                        borderRadius: '6px', cursor: history.length > 0 ? 'pointer' : 'not-allowed', 
                        fontWeight: 'bold', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: '8px',
                        opacity: history.length > 0 ? 1 : 0.6
                    }}
                >
                    <span>📥</span> Export Audit CSV
                </button>
            </div>
            
            <Link to={`/servers/${serverId}`} className="back-link" style={{ display: 'inline-block', marginBottom: '25px', color: '#3182ce', textDecoration: 'none', fontWeight: '500' }}>← Back to Server Details</Link>

            <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: '#f1f5f9' }}>
                        <tr>
                            <th style={{ padding: '15px', textAlign: 'left', color: '#475569', fontSize: '13px' }}>Timestamp</th>
                            <th style={{ padding: '15px', textAlign: 'left', color: '#475569', fontSize: '13px' }}>Action</th>
                            <th style={{ padding: '15px', textAlign: 'left', color: '#475569', fontSize: '13px' }}>Performed By</th>
                            <th style={{ padding: '15px', textAlign: 'left', color: '#475569', fontSize: '13px' }}>Reason & Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan="4" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Loading logs...</td></tr>
                        ) : history.length > 0 ? (
                            history.map(entry => {
                                const isPositive = ['USER_ENABLED', 'SERVER_ACTIVE', 'RENEWED', 'USER_ADDED'].includes(entry.action);
                                const isWarning = ['UPDATED'].includes(entry.action);
                                // Point 1: Distinct styling for System actions
                                const isSystem = entry.performedByUid === 'system_cron';

                                return (
                                    <tr key={entry.id} style={{ 
                                        borderBottom: '1px solid #e2e8f0', 
                                        verticalAlign: 'top',
                                        backgroundColor: isSystem ? '#fdfcf0' : 'transparent' // Light gold tint for system logs
                                    }}>
                                        <td style={{ padding: '15px', fontSize: '13px', whiteSpace: 'nowrap', color: '#1e293b' }}>
                                            {formatDate(entry.timestamp)}
                                        </td>
                                        <td style={{ padding: '15px' }}>
                                            <span style={{ 
                                                backgroundColor: isPositive ? '#dcfce7' : (isWarning ? '#dbeafe' : '#fee2e2'),
                                                color: isPositive ? '#166534' : (isWarning ? '#1e40af' : '#991b1b'),
                                                padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '800', display: 'inline-block'
                                            }}>
                                                {entry.action.replace('_', ' ')}
                                            </span>
                                        </td>
                                        <td style={{ padding: '15px', fontSize: '13px', color: isSystem ? '#b45309' : '#475569', fontWeight: isSystem ? '600' : '400' }}>
                                            {isSystem ? 'SYSTEM AUTO-BOT' : entry.performedBy}
                                        </td>
                                        <td style={{ padding: '15px', maxWidth: '500px' }}>
                                            <div style={{ marginBottom: entry.snapshot ? '12px' : '0', fontSize: '14px', color: '#1e293b', lineHeight: '1.5', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                                                {entry.reason}
                                            </div>
                                            
                                            {/* Tweak 2: Snapshot Accordion */}
                                            {entry.snapshot && (
                                                <details style={{ 
                                                    backgroundColor: '#f8fafc', 
                                                    padding: '10px', 
                                                    borderRadius: '8px', 
                                                    border: '1px solid #e2e8f0' 
                                                }}>
                                                    <summary style={{ 
                                                        cursor: 'pointer', 
                                                        fontWeight: 'bold', 
                                                        color: '#475569', 
                                                        fontSize: '12px',
                                                        outline: 'none',
                                                        listStyle: 'none'
                                                    }}>
                                                        🔍 View Data Snapshot
                                                    </summary>
                                                    <pre style={{ 
                                                        margin: '10px 0 0 0', 
                                                        whiteSpace: 'pre-wrap', 
                                                        fontSize: '11px', 
                                                        color: '#334155',
                                                        backgroundColor: '#ffffff',
                                                        padding: '10px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #f1f5f9',
                                                        fontFamily: 'monospace'
                                                    }}>
                                                        {JSON.stringify(entry.snapshot, null, 2)}
                                                    </pre>
                                                </details>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr><td colSpan="4" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No history logs found.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ServerStatusHistoryPage;